export type AppTheme = "light" | "dark";

export const APP_THEME_STORAGE_KEY = "fdr-e2e-agent-theme";

function isAppTheme(value: string | null): value is AppTheme {
  return value === "light" || value === "dark";
}

export function readStoredTheme(storage: Storage | undefined): AppTheme | null {
  if (!storage) return null;
  try {
    const stored = storage.getItem(APP_THEME_STORAGE_KEY);
    return isAppTheme(stored) ? stored : null;
  } catch {
    return null;
  }
}

export function preferredTheme(
  matcher: Pick<Window, "matchMedia"> | undefined,
): AppTheme {
  if (!matcher || typeof matcher.matchMedia !== "function") return "light";
  return matcher.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

export function resolveInitialTheme(
  browserWindow: Pick<Window, "localStorage" | "matchMedia"> | undefined,
): AppTheme {
  return (
    readStoredTheme(browserWindow?.localStorage) ??
    preferredTheme(browserWindow)
  );
}

export function applyTheme(
  theme: AppTheme,
  documentElement: HTMLElement = document.documentElement,
): void {
  documentElement.dataset.theme = theme;
  documentElement.style.colorScheme = theme;
}

export function storeTheme(
  theme: AppTheme,
  storage: Storage | undefined = window.localStorage,
): void {
  try {
    storage?.setItem(APP_THEME_STORAGE_KEY, theme);
  } catch {
    // Theme changes should still apply when browser storage is blocked.
  }
}

export function initializeTheme(browserWindow: Window = window): AppTheme {
  const theme = resolveInitialTheme(browserWindow);
  applyTheme(theme, browserWindow.document.documentElement);
  return theme;
}
